// frontendPLCalculations.ts
// Frontend P&L calculation service that mirrors UnifiedDetailedIncomeStatement component logic
import { AccountEntry, CategoryTemplate, ViewType } from '../../types';
import {
  getQuarter,
  getYear,
  getYearMonthRange,
  monthMap,
  compareMonthYear,
  calculateGrowthPercentage,
  calculateTTMYoYGrowth,
  calculateYTDGrowthPercentage,
  calculateSectionTotal,
  calculateCategoryTotal,
  calculateTTM,
  calculateTTMForPeriodType,
  calculateRoleBasedEBITDA
} from './calculations';

// Interface matching the backend PLLineItem structure
export interface PLLineItem {
  type: 'section_header' | 'category_header' | 'account' | 'category_total' | 'section_total' | 'calculated_total' | 'calculated_percentage';
  name: string;
  values?: number[];
  level: number;
  category_value?: string;
  account_number?: string;
  is_final?: boolean;
}

export interface PLDetailData {
  periods: string[];
  line_items: PLLineItem[];
}

export class FrontendPLCalculationService {
  /**
   * Build P&L data using frontend calculations that mirror the component's logic
   */
  static buildPLData(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    viewType: ViewType,
    selectedProject: any
  ): PLDetailData {
    if (!accounts.length || !activeTemplate) {
      return { periods: [], line_items: [] };
    }

    // Get all available months from accounts
    const allMonths = [...new Set(accounts.map(acc => acc.month))].sort(compareMonthYear);

    // Always generate monthly data - let the component handle view transformations
    const periods = allMonths;

    // Define sections (matching component logic)
    const sections = [
      {
        title: 'Revenue',
        name: 'Revenue',
        isRevenue: true,
        isContraRevenue: false,
        categories: this.getSectionCategories(activeTemplate, 'Revenue'),
        showTotal: true
      },
      {
        title: 'Sales Discounts and Returns',
        name: 'Sales Discounts and Returns',
        isRevenue: true,
        isContraRevenue: true,
        categories: this.getSectionCategories(activeTemplate, 'Sales Discounts and Returns'),
        showTotal: true
      },
      {
        title: 'Cost of Goods Sold',
        name: 'Cost of Goods Sold',
        isRevenue: false,
        isContraRevenue: false,
        categories: this.getSectionCategories(activeTemplate, 'Cost of Goods Sold'),
        showTotal: true
      },
      {
        title: 'Operating Expenses',
        name: 'Operating Expenses',
        isRevenue: false,
        isContraRevenue: false,
        categories: this.getSectionCategories(activeTemplate, 'Operating Expenses'),
        showTotal: true
      },
      {
        title: 'Other Expenses',
        name: 'Other Income (Expense)',
        isRevenue: false,
        isContraRevenue: false,
        categories: this.getSectionCategories(activeTemplate, 'Other Income (Expense)'),
        showTotal: true
      }
    ];

    const line_items: PLLineItem[] = [];

    // Build line items for each section
    sections.filter(section => section.categories.length > 0).forEach(section => {
      // Section header
      line_items.push({
        type: 'section_header',
        name: section.title,
        level: 1
      });

      // Categories and accounts within section
      section.categories.forEach(categoryValue => {
        const category = activeTemplate.categories.find(cat => cat.value === categoryValue);
        if (!category) return;

        // Category header with values
        const categoryValues = periods.map(period =>
          calculateCategoryTotal(accounts, categoryValue, period, 'monthly', section.isRevenue, section.isContraRevenue)
        );

        line_items.push({
          type: 'category_header',
          name: category.label,
          category_value: categoryValue,
          values: categoryValues,
          level: 2
        });

        // Individual accounts
        const accountNumbers = this.getAccountDisplayOrder(accounts, categoryValue);
        accountNumbers.forEach(accountNumber => {
          const accountName = accounts.find(a => a.accountNumber === accountNumber)?.name || '';
          const accountValues = periods.map(period => {
            const periodAccounts = this.getAccountsByPeriod(accounts, accountNumber, categoryValue, period, 'monthly');
            return periodAccounts.reduce((total, account) => {
              if (section.isRevenue || section.isContraRevenue) {
                return total + (account.credit - account.debit);
              } else {
                return total + (account.debit - account.credit);
              }
            }, 0);
          });

          if (accountValues.some(val => val !== 0)) {
            line_items.push({
              type: 'account',
              name: accountName,
              account_number: accountNumber,
              category_value: categoryValue,
              values: accountValues,
              level: 3
            });
          }
        });

        // Category total
        if (categoryValues.some(val => val !== 0)) {
          line_items.push({
            type: 'category_total',
            name: `Total ${category.label}`,
            values: categoryValues,
            level: 2
          });
        }
      });

      // Section total
      const sectionValues = periods.map(period =>
        calculateSectionTotal(accounts, section.categories, period, 'monthly', section.isRevenue, section.isContraRevenue)
      );

      if (sectionValues.some(val => val !== 0)) {
        line_items.push({
          type: 'section_total',
          name: `Total ${section.title}`,
          values: sectionValues,
          level: 1
        });
      }

      // Add calculated totals after specific sections (matching component logic)
      if (section.title === 'Sales Discounts and Returns') {
        // Net Revenue
        const netRevenueValues = periods.map(period => this.calculateNetRevenueTotal(accounts, sections, period, 'monthly'));
        line_items.push({
          type: 'calculated_total',
          name: 'Net Revenue',
          values: netRevenueValues,
          level: 1
        });

        // Net Revenue Growth %
        const growthValues = periods.map((period, index) => {
          if (index === 0) return null;
          const currentRevenue = this.calculateNetRevenueTotal(accounts, sections, period, 'monthly');
          const previousPeriod = periods[index - 1];
          const previousRevenue = this.calculateNetRevenueTotal(accounts, sections, previousPeriod, 'monthly');
          return parseFloat(calculateGrowthPercentage(currentRevenue, previousRevenue).replace(/[+%]/g, ''));
        });

        line_items.push({
          type: 'calculated_percentage',
          name: 'Net Revenue Growth %',
          values: growthValues.map(val => val ?? 0),
          level: 1
        });
      }

      if (section.title === 'Cost of Goods Sold') {
        // Gross Margin
        const grossMarginValues = periods.map(period => this.calculateGrossMargin(accounts, sections, period, 'monthly'));
        line_items.push({
          type: 'calculated_total',
          name: 'Gross Margin',
          values: grossMarginValues,
          level: 1
        });

        // Gross Margin %
        const marginPercentValues = periods.map(period => {
          const netRevenue = this.calculateNetRevenueTotal(accounts, sections, period, 'monthly');
          const grossMargin = this.calculateGrossMargin(accounts, sections, period, 'monthly');
          return netRevenue !== 0 ? (grossMargin / netRevenue) * 100 : 0;
        });

        line_items.push({
          type: 'calculated_percentage',
          name: 'Gross Margin %',
          values: marginPercentValues,
          level: 1
        });
      }

      if (section.title === 'Operating Expenses') {
        // Operating Income
        const operatingIncomeValues = periods.map(period => this.calculateOperatingIncome(accounts, sections, period, 'monthly'));
        line_items.push({
          type: 'calculated_total',
          name: 'Operating Income',
          values: operatingIncomeValues,
          level: 1
        });
      }
    });

    // Net Income
    const netIncomeValues = periods.map(period => this.calculateNetIncome(accounts, sections, period, 'monthly'));
    line_items.push({
      type: 'calculated_total',
      name: 'Net Income',
      values: netIncomeValues,
      level: 0,
      is_final: true
    });

    // EBITDA (matching reportBuilder.ts logic)
    const ebitdaValues = periods.map((period, idx) => {
      const netIncome = netIncomeValues[idx];
      return this.calculateEBITDA(accounts, activeTemplate, period, netIncome);
    });
    line_items.push({
      type: 'calculated_total',
      name: 'EBITDA',
      values: ebitdaValues,
      level: 0
    });

    // EBITDA Margin %
    const ebitdaMarginValues = periods.map((period, idx) => {
      const netRevenue = this.calculateNetRevenueTotal(accounts, sections, period, 'monthly');
      const ebitda = ebitdaValues[idx];
      if (netRevenue === 0) return null;
      return Math.round((ebitda / netRevenue) * 1000) / 10;
    });
    line_items.push({
      type: 'calculated_percentage',
      name: 'EBITDA Margin %',
      values: ebitdaMarginValues.map(val => val ?? 0),
      level: 0
    });

    return {
      periods,
      line_items
    };
  }

  /**
   * Get periods for the specified view type
   */
  private static getPeriodsForView(allMonths: string[], viewType: ViewType): string[] {
    if (viewType === 'monthly') {
      return allMonths;
    } else if (viewType === 'quarterly') {
      return [...new Set(allMonths.map(getQuarter))].sort((a, b) => {
        const [q1, y1] = a.split("'");
        const [q2, y2] = b.split("'");
        return y1 === y2
          ? parseInt(q1.slice(1)) - parseInt(q2.slice(1))
          : parseInt(y1) - parseInt(y2);
      });
    } else {
      return [...new Set(allMonths.map(getYear))].sort();
    }
  }

  /**
   * Get section categories (placeholder - would need to match component logic)
   */
  private static getSectionCategories(template: CategoryTemplate, sectionTitle: string): string[] {
    // This maps section titles to category types - matching component logic
    const sectionMapping: Record<string, string[]> = {
      'Revenue': ['revenue'],
      'Sales Discounts and Returns': ['contra-revenue'],
      'Cost of Goods Sold': ['cogs', 'contra-cogs'],
      'Operating Expenses': ['operating-expense'],
      'Other Income (Expense)': ['interest-expense', 'income-tax-expense', 'other-expense']
    };

    const types = sectionMapping[sectionTitle] || [];
    return template.categories
      .filter(cat => types.includes(cat.type))
      .map(cat => cat.value);
  }

  /**
   * Get account display order for a category
   */
  private static getAccountDisplayOrder(accounts: AccountEntry[], categoryValue: string): string[] {
    const categoryAccounts = accounts.filter(acc => acc.category === categoryValue);
    const uniqueAccountNumbers = [...new Set(categoryAccounts.map(acc => acc.accountNumber))]
      .filter((acc): acc is string => typeof acc === 'string' && !!acc);

    return uniqueAccountNumbers.sort((a, b) => {
      const aNum = parseInt(a);
      const bNum = parseInt(b);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  /**
   * Get accounts for a specific period
   */
  private static getAccountsByPeriod(
    accounts: AccountEntry[],
    accountNumber: string,
    categoryValue: string,
    period: string,
    viewType: ViewType
  ): AccountEntry[] {
    if (viewType === 'monthly') {
      return accounts.filter(account =>
        account.accountNumber === accountNumber &&
        account.category === categoryValue &&
        account.month === period
      );
    } else if (viewType === 'quarterly') {
      return accounts.filter(account =>
        account.accountNumber === accountNumber &&
        account.category === categoryValue &&
        getQuarter(account.month) === period
      );
    } else {
      return accounts.filter(account =>
        account.accountNumber === accountNumber &&
        account.category === categoryValue &&
        getYear(account.month) === period
      );
    }
  }

  /**
   * Calculate Net Revenue Total
   */
  private static calculateNetRevenueTotal(
    accounts: AccountEntry[],
    sections: any[],
    period: string,
    viewType: ViewType
  ): number {
    const revenueSection = sections.find(s => s.title === 'Revenue');
    const contraRevenueSection = sections.find(s => s.title === 'Sales Discounts and Returns');

    const revenue = revenueSection ?
      calculateSectionTotal(accounts, revenueSection.categories, period, viewType, true, false) : 0;
    const contraRevenue = contraRevenueSection ?
      calculateSectionTotal(accounts, contraRevenueSection.categories, period, viewType, true, true) : 0;

    return revenue + contraRevenue;
  }

  /**
   * Calculate EBITDA using the proper role-based approach from calculations.ts
   */
  private static calculateEBITDA(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    period: string,
    netIncome: number
  ): number {
    // Use the established role-based EBITDA calculation from calculations.ts
    return calculateRoleBasedEBITDA(
      accounts,
      activeTemplate.categories,
      period,
      'monthly',
      netIncome
    );
  }

  /**
   * Calculate Gross Margin
   */
  private static calculateGrossMargin(
    accounts: AccountEntry[],
    sections: any[],
    period: string,
    viewType: ViewType
  ): number {
    const netRevenue = this.calculateNetRevenueTotal(accounts, sections, period, viewType);
    const cogsSection = sections.find(s => s.title === 'Cost of Goods Sold');
    const cogs = cogsSection ?
      calculateSectionTotal(accounts, cogsSection.categories, period, viewType, false, false) : 0;

    return netRevenue - cogs;
  }

  /**
   * Calculate Operating Income
   */
  private static calculateOperatingIncome(
    accounts: AccountEntry[],
    sections: any[],
    period: string,
    viewType: ViewType
  ): number {
    const grossMargin = this.calculateGrossMargin(accounts, sections, period, viewType);
    const opExpSection = sections.find(s => s.title === 'Operating Expenses');
    const operatingExpenses = opExpSection ?
      calculateSectionTotal(accounts, opExpSection.categories, period, viewType, false, false) : 0;

    return grossMargin - operatingExpenses;
  }

  /**
   * Calculate Net Income
   */
  private static calculateNetIncome(
    accounts: AccountEntry[],
    sections: any[],
    period: string,
    viewType: ViewType
  ): number {
    const operatingIncome = this.calculateOperatingIncome(accounts, sections, period, viewType);
    const otherExpSection = sections.find(s => s.title === 'Other Expenses');
    const otherExpenses = otherExpSection ?
      calculateSectionTotal(accounts, otherExpSection.categories, period, viewType, false, false) : 0;

    return operatingIncome - otherExpenses;
  }

  /**
   * Calculate TTM values for yearly view
   */
  static calculateTTMValues(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    allMonths: string[],
    selectedProject: any
  ) {
    const projectPeriodType = selectedProject?.period_type || 'monthly';

    // TTM Net Revenue
    const ttmNetRevenue = this.calculateTTMSectionTotal(
      accounts,
      this.getSectionCategories(activeTemplate, 'Revenue'),
      allMonths,
      true,
      false,
      projectPeriodType
    ) + this.calculateTTMSectionTotal(
      accounts,
      this.getSectionCategories(activeTemplate, 'Sales Discounts and Returns'),
      allMonths,
      true,
      true,
      projectPeriodType
    );

    // TTM Gross Margin
    const ttmCOGS = this.calculateTTMSectionTotal(
      accounts,
      this.getSectionCategories(activeTemplate, 'Cost of Goods Sold'),
      allMonths,
      false,
      false,
      projectPeriodType
    );
    const ttmGrossMargin = ttmNetRevenue - ttmCOGS;

    // TTM Operating Income
    const ttmOperatingExpenses = this.calculateTTMSectionTotal(
      accounts,
      this.getSectionCategories(activeTemplate, 'Operating Expenses'),
      allMonths,
      false,
      false,
      projectPeriodType
    );
    const ttmOperatingIncome = ttmGrossMargin - ttmOperatingExpenses;

    // TTM Net Income
    const ttmOtherExpenses = this.calculateTTMSectionTotal(
      accounts,
      this.getSectionCategories(activeTemplate, 'Other Income (Expense)'),
      allMonths,
      false,
      false,
      projectPeriodType
    );
    const ttmNetIncome = ttmOperatingIncome - ttmOtherExpenses;

    return {
      ttmNetRevenue,
      ttmGrossMargin,
      ttmOperatingIncome,
      ttmNetIncome
    };
  }

  /**
   * Calculate TTM for section totals
   */
  private static calculateTTMSectionTotal(
    accounts: AccountEntry[],
    categories: string[],
    allMonths: string[],
    isRevenue: boolean,
    isContraRevenue: boolean,
    projectPeriodType: string
  ): number {
    return categories.reduce((total, category) => {
      return total + calculateTTMForPeriodType(
        accounts,
        category,
        allMonths,
        isRevenue,
        isContraRevenue,
        projectPeriodType as 'monthly' | 'quarterly' | 'annual'
      );
    }, 0);
  }
}